// frontend/src/context/PrescriptionContext.js
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { httpService } from "../../services/httpService";
import { makeMedId } from "./MedicationRow";

const PrescriptionCtx = createContext(null);

export const PrescriptionProvider = ({ children, initialData, refId, orderId, vendorPetId }) => {
  const initialMeds = initialData?.plan?.prescription?.medications || [];
  const initialLabs = initialData?.objective?.labTests || [];
  const initialNotes = initialData?.plan?.prescription?.notes || "";
  const initialPrescriptionRef = initialData?.plan?.prescriptionRef || null;
  
  // Add recommendations state
  const initialRecommendations = {
    quickRecommendations: initialData?.plan?.recommendations?.quickRecommendations || [],
    notes: initialData?.plan?.recommendations?.notes || "",
  };

  const [medicationsList, setMedicationsList] = useState(() =>
    initialMeds.map((m) => ({
      id: makeMedId(),
      drugName: m.drugName || "",
      selectedMed: null,
      product_id: m.product_id || null,
      morning: m.morning || 0,
      afternoon: m.afternoon || 0,
      night: m.night || 0,
      foodTiming: m.foodTiming || "",
      duration: m.duration ?? 1,
      unit: m.unit || "",
      note: m.note || "",
      sku_number: m.sku_number || "",
      sale_price: m.sale_price || 0,
      mrp: m.mrp || 0,
      gst_slab: m.gst_slab || 0,
      brand: m.brand || "",
    }))
  );

  const [labTests, setLabTests] = useState(() => initialLabs);
  const [notes, setNotes] = useState(initialNotes);
  const [prescriptionRef, setPrescriptionRef] = useState(initialPrescriptionRef);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  
  // Recommendations state
  const [recommendations, setRecommendations] = useState(initialRecommendations);
  const hasUserEditedMedsRef = useRef(false);

  const mapMedication = useCallback((m) => ({
    id: makeMedId(),
    drugName: m.drugName || "",
    selectedMed: null,
    product_id: m.product_id || null,
    morning: m.morning || 0,
    afternoon: m.afternoon || 0,
    night: m.night || 0,
    foodTiming: m.foodTiming || "",
    duration: m.duration ?? 1,
    unit: m.unit || "",
    note: m.note || "",
    sku_number: m.sku_number || "",
    sale_price: m.sale_price || 0,
    mrp: m.mrp || 0,
    gst_slab: m.gst_slab || 0,
    brand: m.brand || "",
  }), []);

  // Hydrate medications when SOAP data loads after the provider mounts
  useEffect(() => {
    if (hasUserEditedMedsRef.current) return;
    const incomingMeds = initialData?.plan?.prescription?.medications || [];
    if (incomingMeds.length === 0) return;
    setMedicationsList((prev) => {
      if (prev.length > 0) return prev;
      return incomingMeds.map(mapMedication);
    });
  }, [initialData, mapMedication]);

  useEffect(() => {
    if (hasUserEditedMedsRef.current) return;
    const incomingNotes = initialData?.plan?.prescription?.notes;
    if (incomingNotes) setNotes(incomingNotes);
    const incomingRef = initialData?.plan?.prescriptionRef;
    if (incomingRef) setPrescriptionRef(incomingRef);
  }, [initialData]);

  const validMedications = medicationsList.filter((m) => m.drugName?.trim());
  const canGenerate = validMedications.length > 0 || labTests.length > 0;

  const generatePrescription = useCallback(async () => {
    setGenerateError("");

    if (validMedications.length === 0 && labTests.length === 0) {
      setGenerateError("Add at least one medication or lab test before generating.");
      return null;
    }

    // Log current recommendations before sending
    console.log('📋 Current recommendations in context:', recommendations);

    setIsGenerating(true);
    try {
      const payload = {
        vendor_appointment_id: refId || null,
        order_id: orderId || null,
        vendor_pet_id: vendorPetId || null,
        medicationsList: validMedications.map((m) => ({
          drugName: m.drugName,
          product_id: m.product_id || null,
          unit: m.unit || "",
          morning: parseFloat(m.morning) || 0,
          afternoon: parseFloat(m.afternoon) || 0,
          night: parseFloat(m.night) || 0,
          foodTiming: m.foodTiming || "",
          duration: parseInt(m.duration, 10) || 1,
          note: m.note || "",
          sku_number: m.sku_number || "",
          sale_price: m.sale_price || 0,
          mrp: m.mrp || 0,
          gst_slab: m.gst_slab || 0,
          brand: m.brand || "",
        })),
        labTestsList: labTests.map((t) => ({
          lab_test_id: t.lab_test_id,
          price: t.price || 0,
        })),
        notes,
        recommendations: {
          quickRecommendations: recommendations.quickRecommendations || [],
          notes: recommendations.notes || "",
        },
      };

      console.log('📤 Sending payload with recommendations:', payload.recommendations);

      const response = await httpService.post("/prescription/generatePrescription", payload);

      if (response.code === 200 && response.data?.prescription_url) {
        window.open(response.data.prescription_url, "_blank", "noopener,noreferrer");
        if (response.data.prescription_id) setPrescriptionRef(response.data.prescription_id);
        return response.data;
      }

      setGenerateError(response.message || "Failed to generate prescription.");
      return null;
    } catch (error) {
      console.error("❌ Error generating prescription:", error);
      setGenerateError(error.response?.data?.message || "Something went wrong while generating the prescription.");
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [validMedications, labTests, notes, refId, orderId, vendorPetId, recommendations]);

  const value = {
    medicationsList,
    setMedicationsList: (updater) => {
      hasUserEditedMedsRef.current = true;
      setMedicationsList(updater);
    },
    labTests,
    setLabTests,
    notes,
    setNotes,
    prescriptionRef,
    canGenerate,
    isGenerating,
    generateError,
    generatePrescription,
    // Expose recommendations and setter
    recommendations,
    setRecommendations,
  };

  return <PrescriptionCtx.Provider value={value}>{children}</PrescriptionCtx.Provider>;
};

export const usePrescription = () => {
  const ctx = useContext(PrescriptionCtx);
  if (!ctx) {
    throw new Error("usePrescription must be used within a PrescriptionProvider");
  }
  return ctx;
};